import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSupplier } from "@/context/SupplierContext";
import { fmtDateTime } from "@/lib/format";
import { MessageCircle, Mail, MessageSquare } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CHANNEL_META = {
  whatsapp: { icon: MessageCircle, cls: "bg-green-50 text-green-700 border-green-200", label: "WhatsApp" },
  email: { icon: Mail, cls: "bg-blue-50 text-blue-700 border-blue-200", label: "Email" },
  platform: { icon: MessageSquare, cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Platform" },
};

function commStatus(c) {
  if (c.direction === "supplier_to_buyer") return { label: "Responded", cls: "bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]" };
  if (c.delivery_status === "simulated" || c.delivery_status === "logged") return { label: "Logged", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" };
  return { label: "Sent", cls: "bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]" };
}

export default function Communications() {
  const { user } = useAuth();
  const supplierCtx = useSupplier(); // null outside SupplierProvider
  const isSupplier = user?.role === "supplier";
  const [comms, setComms] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [channel, setChannel] = useState("all");
  const [supplier, setSupplier] = useState("all");
  const [poQuery, setPoQuery] = useState("");
  const navigate = useNavigate();
  const base = user?.role === "supplier" ? "/seller/pos" : user?.role === "org_admin" ? "/admin/pos" : "/buyer/pos";
  const buyerCtx = supplierCtx?.selected;
  const orgName = (orgId) => supplierCtx?.buyers.find((b) => b.org_id === orgId)?.name || "";

  useEffect(() => {
    if (!isSupplier) {
      api.get("/suppliers").then((r) => setSuppliers(r.data.suppliers)).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    if (isSupplier && !supplierCtx?.loaded) return;
    const qs = new URLSearchParams();
    if (channel !== "all") qs.set("channel", channel);
    if (supplier !== "all") qs.set("supplier_id", supplier);
    if (isSupplier && buyerCtx) qs.set("buyer_org", buyerCtx);
    api.get(`/communications?${qs}`).then((r) => setComms(r.data.communications)).catch(() => {});
  }, [channel, supplier, buyerCtx, supplierCtx?.loaded]);

  const filtered = poQuery.trim()
    ? comms.filter((c) => (c.po_number || "").toLowerCase().includes(poQuery.trim().toLowerCase()))
    : comms;

  return (
    <div data-testid="communications-page" className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Communications</h1>
        <p className="text-sm text-muted-foreground">Every WhatsApp, email and platform message — recorded against its PO. Mocked channels are marked "Logged", never "Delivered".</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
        <Input data-testid="comm-po-filter" value={poQuery} onChange={(e) => setPoQuery(e.target.value)} placeholder="Filter by PO number…" className="w-56 font-mono" />
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger data-testid="channel-filter" className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All channels</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
            <SelectItem value="email">Email</SelectItem>
            <SelectItem value="platform">Platform</SelectItem>
          </SelectContent>
        </Select>
        {user?.role !== "supplier" && (
          <Select value={supplier} onValueChange={setSupplier}>
            <SelectTrigger data-testid="comm-supplier-filter" className="w-56"><SelectValue placeholder="Supplier" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All suppliers</SelectItem>
              {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <p className="ml-auto text-sm text-muted-foreground">{filtered.length} messages</p>
      </div>

      <div className="divide-y rounded-lg border bg-card" data-testid="communications-list">
        {filtered.length === 0 && <p className="p-6 text-sm text-muted-foreground">No communications found.</p>}
        {filtered.map((c) => {
          const meta = CHANNEL_META[c.channel] || CHANNEL_META.platform;
          const st = commStatus(c);
          return (
            <button key={c.id} data-testid={`comm-row-${c.id.slice(0, 8)}`} onClick={() => c.po_id && navigate(`${base}/${c.po_id}`)} className="flex w-full items-start gap-3 px-5 py-3.5 text-left transition-colors hover:bg-slate-50">
              <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${meta.cls}`}>
                <meta.icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  {user?.role === "supplier" && c.buyer_organization_id && (
                    <span data-testid={`comm-org-${c.id.slice(0, 8)}`} className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-white">{orgName(c.buyer_organization_id)}</span>
                  )}
                  <span className="font-mono font-semibold text-slate-700">{c.po_number || "—"}</span>
                  <span>·</span><span>{c.sender_name}</span>
                  <span>·</span><span>{c.direction === "buyer_to_supplier" ? "Buyer → Supplier" : c.direction === "system" ? "System" : "Supplier → Buyer"}</span>
                  <span className="ml-auto font-mono">{fmtDateTime(c.created_at)}</span>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm">{c.message}</p>
                <span className={`mt-1.5 inline-block rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${st.cls}`}>{st.label} · {meta.label}</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
